import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ENTRY_POPUP from "../api/services/ENTRY_POPUP";
import {
  navigateToScreenKey,
  resolveScreenKeyPath,
} from "../utils/entryPopupScreens";

/**
 * App-entry promo popup — the dashboard port of the Admin-App's launch popup
 * (Admin-App/src/whatsNew/entryPopup.js + EntryPopupModal.js).
 *
 * A super-admin-controlled carousel shown ONCE per campaign. The last campaign
 * a user actually saw is kept in localStorage; the popup re-opens only when the
 * server's campaignVersion is STRICTLY greater — so it never flashes on every
 * page load, and a "re-trigger" (version bump) reaches even users who already
 * dismissed the previous concept.
 *
 * Everything fails soft, by design: a failed/slow fetch, a broken image, an
 * unknown screenKey or a telemetry error all end in "render nothing". A broken
 * promo must never look like a broken app, and it must never block first paint —
 * the fetch starts after mount and the popup only appears 700ms later.
 *
 * NOT mounted here — see the note at the bottom of the file.
 */

export const ENTRY_POPUP_SEEN_KEY = "entryPopupSeenVersion";

// Matches the mobile app: let the dashboard paint before the promo appears.
const OPEN_DELAY_MS = 700;

// Brand palette (tor.digital) — same colours the mobile popup uses.
const NAVY = "#060A35";
const PINK = "#E93D67";
const WHITE = "#FFFFFF";

/* ─────────────────────────── pure helpers ─────────────────────────────── */

/** True when the config carries at least one showable slide. */
export function hasShowableContent(popup) {
  if (!popup) return false;
  if (Array.isArray(popup.slides) && popup.slides.some((s) => s && s.imageUrl)) {
    return true;
  }
  return !!popup.imageUrl; // legacy single-image backend
}

/**
 * The version gate. A missing/blank/garbage seen value counts as 0, so a first
 * visit always sees campaignVersion >= 1. ONE version gates the whole carousel.
 */
export function shouldShowEntryPopup(popup, seenVersion) {
  if (!popup || !popup.enabled || !hasShowableContent(popup)) return false;
  const version = Number(popup.campaignVersion) || 0;
  const seen = Number(seenVersion) || 0;
  return version > seen;
}

/**
 * Normalise the config into a slides array. Prefers `slides`; folds a legacy
 * top-level `imageUrl` into a single slide so an older backend still works.
 */
export function toSlides(popup) {
  if (!popup) return [];
  if (Array.isArray(popup.slides) && popup.slides.length) {
    return popup.slides.filter((s) => s && s.imageUrl);
  }
  if (popup.imageUrl) {
    return [
      {
        imageUrl: popup.imageUrl,
        actionType: popup.linkUrl ? "external" : "none",
        linkUrl: popup.linkUrl || "",
        screenKey: "",
      },
    ];
  }
  return [];
}

const readSeenVersion = () => {
  try {
    return window.localStorage.getItem(ENTRY_POPUP_SEEN_KEY);
  } catch (_e) {
    return null; // private mode / storage disabled → treat as "never seen"
  }
};

const writeSeenVersion = (version) => {
  try {
    if (version != null) {
      window.localStorage.setItem(ENTRY_POPUP_SEEN_KEY, String(version));
    }
  } catch (_e) {
    // worst case the popup shows once more next visit
  }
};

/** Logged in? Same signal the API client uses to authenticate requests. */
const hasSession = () => {
  try {
    return !!window.localStorage.getItem("token");
  } catch (_e) {
    return false;
  }
};

/* ──────────────────────────── the hook ────────────────────────────────── */

/**
 * Fetches the campaign once, decides whether to show it, and exposes a `close`
 * that persists the seen version. Never throws.
 */
function useEntryPopup() {
  const [popup, setPopup] = useState(null);
  const [visible, setVisible] = useState(false);
  const fired = useRef(false);
  const campaignRef = useRef(null);
  const { pathname } = useLocation();

  // Fire-and-forget one analytics row for the campaign actually on screen.
  const track = useCallback((type, extra = {}) => {
    try {
      const campaignVersion = campaignRef.current;
      if (campaignVersion == null) return;
      ENTRY_POPUP.send_events([
        {
          type,
          campaignVersion,
          slideIndex: extra.slideIndex == null ? null : extra.slideIndex,
          actionType: extra.actionType || "",
        },
      ]);
    } catch (_e) {
      // telemetry is never allowed to reach the UI
    }
  }, []);

  // Runs on every route change but fetches at most once (`fired`). Re-checking
  // the gate per route is what lets the popup appear right after login, when
  // the very first render happened on /auth/login with no token yet.
  useEffect(() => {
    if (fired.current) return undefined;
    // Never over the auth screens, and never without a session: the events
    // endpoint needs a business JWT, and a promo over the login form is wrong.
    if (!pathname || pathname.startsWith("/auth")) return undefined;
    if (!hasSession()) return undefined;

    let alive = true;
    let timer = null;

    (async () => {
      try {
        const res = await ENTRY_POPUP.get_popup();
        const data = res && res.data;
        if (!alive || !data) return;

        if (!shouldShowEntryPopup(data, readSeenVersion())) return;
        if (fired.current) return;
        fired.current = true;

        setPopup(data);
        campaignRef.current =
          data.campaignVersion == null ? null : data.campaignVersion;

        timer = setTimeout(() => {
          if (!alive) return;
          setVisible(true);
          track("view"); // once per open, tagged with the campaign shown
        }, OPEN_DELAY_MS);
      } catch (_e) {
        // fail silent — a promo is never worth a broken dashboard
      }
    })();

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [pathname, track]);

  const close = useCallback(() => {
    setVisible(false);
    track("close");
    if (popup) writeSeenVersion(popup.campaignVersion);
  }, [popup, track]);

  return { popup, visible, close, track };
}

/* ──────────────────────────── styles ──────────────────────────────────── */
// Inline on purpose: this component owns no .scss module, so it stays fully
// self-contained and cannot be broken by (or break) the shared stylesheets.

const S = {
  backdrop: {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "rgba(6,10,53,0.82)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // Above the guided-tour overlays (9998-10000), below the app's own
    // action modals (9999999999) so a real dialog always wins.
    zIndex: 10001,
    direction: "rtl",
    animation: "torEntryPopupFade 180ms ease-out",
  },
  column: {
    position: "relative",
    width: "min(92vw, 420px)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  topRow: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-start",
    marginBottom: 10,
  },
  closeBtn: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    border: "none",
    backgroundColor: "rgba(255,255,255,0.18)",
    color: WHITE,
    fontSize: 22,
    lineHeight: "22px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  },
  viewport: {
    position: "relative",
    width: "100%",
    height: "min(62vh, 630px)",
    borderRadius: 24,
    overflow: "hidden",
    backgroundColor: NAVY,
    direction: "rtl",
  },
  // RTL: with direction:rtl the first slide sits at the right edge and the next
  // ones extend to the LEFT (overflowing off-screen), so advancing means
  // sliding the track RIGHTWARDS. The track stays exactly ONE viewport wide so
  // both the slides' `flex-basis:100%` and `translateX(n * 100%)` resolve
  // against the viewport width; widening it to n*100% would make every slide
  // n screens wide.
  track: {
    display: "flex",
    width: "100%",
    height: "100%",
    transition: "transform 280ms ease",
  },
  slide: {
    flex: "0 0 100%",
    width: "100%",
    height: "100%",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
    userSelect: "none",
  },
  spinner: {
    position: "absolute",
    width: 34,
    height: 34,
    borderRadius: "50%",
    border: `3px solid rgba(255,255,255,0.25)`,
    borderTopColor: PINK,
    animation: "torEntryPopupSpin 800ms linear infinite",
    pointerEvents: "none",
  },
  arrow: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: 44,
    height: 44,
    borderRadius: "50%",
    border: "none",
    backgroundColor: "rgba(6,10,53,0.55)",
    color: WHITE,
    fontSize: 22,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  },
  dotsWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 14,
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: "rgba(255,255,255,0.35)",
    transition: "width 200ms ease, background-color 200ms ease",
  },
  dotActive: {
    width: 20,
    backgroundColor: PINK,
  },
  ctaBtn: {
    marginTop: 14,
    padding: "12px 40px",
    borderRadius: 26,
    border: `1.5px solid ${PINK}`,
    backgroundColor: "rgba(233,61,103,0.14)",
    color: WHITE,
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
};

const KEYFRAMES = `
@keyframes torEntryPopupSpin { to { transform: rotate(360deg); } }
@keyframes torEntryPopupFade { from { opacity: 0; } to { opacity: 1; } }
`;

/* ─────────────────────────── the carousel ─────────────────────────────── */

/**
 * One slide. Every card is clickable so a 'click' is logged even for 'none'
 * slides (a click there still tells us the slide drew interest); the action
 * itself is decided upstream in handleSlideClick.
 */
const Slide = ({ slide, interactive, onClick, onImageError }) => {
  const [loading, setLoading] = useState(true);

  return (
    <div style={S.slide}>
      {loading && <div style={S.spinner} />}
      <img
        src={slide.imageUrl}
        alt=""
        style={{ ...S.image, cursor: interactive ? "pointer" : "default" }}
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          onImageError();
        }}
        onClick={onClick}
        draggable={false}
      />
    </div>
  );
};

const EntryPopupModal = ({ slides, onClose, onTrack }) => {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  // Images that failed to load, keyed by their ORIGINAL slide index.
  const [failed, setFailed] = useState({});
  const touchStartX = useRef(null);

  // Keep each slide's original index for analytics even after a broken image
  // is dropped, so per-slide numbers still line up with the configured slides.
  const usable = useMemo(
    () =>
      slides
        .map((slide, i) => ({ slide, originalIndex: i }))
        .filter((s) => !failed[s.originalIndex]),
    [slides, failed]
  );

  const count = usable.length;

  useEffect(() => {
    if (index > count - 1) setIndex(Math.max(0, count - 1));
  }, [count, index]);

  const goNext = useCallback(() => {
    setIndex((i) => Math.min(i + 1, count - 1));
  }, [count]);

  const goPrev = useCallback(() => {
    setIndex((i) => Math.max(i - 1, 0));
  }, []);

  // Esc closes; arrows page (RTL: ← advances, → goes back).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goNext();
      else if (e.key === "ArrowRight") goPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, goNext, goPrev]);

  // Lock background scroll while the promo is up; always restored on unmount.
  useEffect(() => {
    let previous = "";
    try {
      previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    } catch (_e) {
      /* noop */
    }
    return () => {
      try {
        document.body.style.overflow = previous;
      } catch (_e) {
        /* noop */
      }
    };
  }, []);

  const handleSlideClick = useCallback(
    (slide, originalIndex) => {
      if (!slide) return;
      // Log FIRST, for every slide, before any side-effect. Fire-and-forget.
      onTrack("click", {
        slideIndex: originalIndex,
        actionType: slide.actionType || "none",
      });

      if (slide.actionType === "external" && slide.linkUrl) {
        try {
          window.open(slide.linkUrl, "_blank", "noopener,noreferrer");
        } catch (_e) {
          /* a blocked popup is not an app error */
        }
        return;
      }

      if (slide.actionType === "screen" && slide.screenKey) {
        // Resolve BEFORE dismissing: a key with no web twin stays a no-op and
        // leaves the popup open rather than closing it for nothing.
        if (!resolveScreenKeyPath(slide.screenKey)) return;
        onClose();
        navigateToScreenKey(navigate, slide.screenKey);
      }
      // 'none' → nothing beyond the analytics row above.
    },
    [navigate, onClose, onTrack]
  );

  const markFailed = useCallback((originalIndex) => {
    setFailed((prev) =>
      prev[originalIndex] ? prev : { ...prev, [originalIndex]: true }
    );
  }, []);

  // Every image broke → behave exactly like "no campaign".
  if (count === 0) return null;

  const multi = count > 1;
  const safeIndex = Math.min(index, count - 1);

  return (
    <div
      style={S.backdrop}
      onClick={onClose}
      role="presentation"
      onTouchStart={(e) => {
        touchStartX.current = e.touches && e.touches[0] ? e.touches[0].clientX : null;
      }}
      onTouchEnd={(e) => {
        const start = touchStartX.current;
        touchStartX.current = null;
        if (start == null || !e.changedTouches || !e.changedTouches[0]) return;
        const delta = e.changedTouches[0].clientX - start;
        if (Math.abs(delta) < 50) return;
        // RTL: the next slide lives to the LEFT, so dragging content rightwards
        // brings it into view.
        if (delta > 0) goNext();
        else goPrev();
      }}
    >
      <style>{KEYFRAMES}</style>

      {/* Content column — clicks inside must not reach the dismissing backdrop */}
      <div style={S.column} onClick={(e) => e.stopPropagation()}>
        <div style={S.topRow}>
          <button
            type="button"
            style={S.closeBtn}
            onClick={onClose}
            aria-label="סגירה"
          >
            &times;
          </button>
        </div>

        <div style={S.viewport}>
          <div
            style={{
              ...S.track,
              transform: `translateX(${safeIndex * 100}%)`,
            }}
          >
            {usable.map(({ slide, originalIndex }) => {
              const interactive =
                (slide.actionType === "external" && !!slide.linkUrl) ||
                (slide.actionType === "screen" &&
                  !!resolveScreenKeyPath(slide.screenKey));
              return (
                <Slide
                  key={originalIndex}
                  slide={slide}
                  interactive={interactive}
                  onClick={() => handleSlideClick(slide, originalIndex)}
                  onImageError={() => markFailed(originalIndex)}
                />
              );
            })}
          </div>

          {/* Paging arrows. In RTL the NEXT slide sits visually to the LEFT, so
              the advance arrow lives on the card's physical left edge. */}
          {multi && safeIndex < count - 1 && (
            <button
              type="button"
              style={{ ...S.arrow, left: 8 }}
              onClick={goNext}
              aria-label="הבא"
            >
              &#8249;
            </button>
          )}
          {multi && safeIndex > 0 && (
            <button
              type="button"
              style={{ ...S.arrow, right: 8 }}
              onClick={goPrev}
              aria-label="הקודם"
            >
              &#8250;
            </button>
          )}
        </div>

        {multi && (
          <div style={S.dotsWrap}>
            {usable.map(({ originalIndex }, i) => (
              <span
                key={originalIndex}
                style={{ ...S.dot, ...(i === safeIndex ? S.dotActive : null) }}
              />
            ))}
          </div>
        )}

        <button type="button" style={S.ctaBtn} onClick={onClose}>
          הבנתי
        </button>
      </div>
    </div>
  );
};

/* ────────────────────── boundary + auto-opener ────────────────────────── */

/**
 * Last line of defence: ANY render error inside the promo collapses to null.
 * "A broken promo must never look like a broken app" is a hard requirement, and
 * a thrown render would otherwise take the whole route tree down with it.
 */
class EntryPopupBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    // swallowed on purpose — nothing to recover, nothing to tell the user
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

const EntryPopupAutoOpener = () => {
  const { popup, visible, close, track } = useEntryPopup();
  const slides = useMemo(() => toSlides(popup), [popup]);

  if (!popup || !visible || slides.length === 0) return null;
  return <EntryPopupModal slides={slides} onClose={close} onTrack={track} />;
};

/**
 * Drop-in, self-contained mount. Renders nothing until (and unless) there is a
 * fresh campaign to show.
 *
 * MOUNTING (not done here — the integration step owns it): render <EntryPopup />
 * once, app-wide, INSIDE the <Router> in src/routes/routes.jsx — next to
 * <TourOverlays /> is the right spot. It needs Router context (useNavigate /
 * useLocation) and must live outside <Layout>, which is re-mounted per route.
 */
const EntryPopup = () => (
  <EntryPopupBoundary>
    <EntryPopupAutoOpener />
  </EntryPopupBoundary>
);

export default EntryPopup;
